import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './AdminSidebar.css';

type AdminView =
  | 'overview'
  | 'applications'
  | 'users'
  | 'payments'
  | 'documents'
  | 'mandates'
  | 'reports'
  | 'settings';

interface AdminSidebarProps {
  currentView: AdminView;
  onViewChange: (view: AdminView) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isMobileDrawer?: boolean;
}

interface NavItem {
  id: AdminView;
  label: string;
  icon: string;
  badge?: number;
  description: string;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  currentView,
  onViewChange,
  collapsed,
  onToggleCollapse,
  isMobileDrawer = false
}) => {
  const showLabels = !collapsed || isMobileDrawer;
  const [badges, setBadges] = useState({
    applications: 0,
    payments: 0,
    documents: 0
  });
  const [quickStats, setQuickStats] = useState({
    activeLoans: 0,
    outstanding: 0,
    repaymentRate: 0,
  });

  // Fetch badge counts from API
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        // Fetch dashboard overview for counts
        const response = await adminAPI.getDashboardOverview();
        if (response.success && response.data) {
          const data = response.data;
          setBadges({
            applications: data.pendingApplications || 0,
            payments: 0, // Will be updated from payments API
            documents: 0 // Will be updated from documents API
          });
          setQuickStats({
            activeLoans: data.activeLoans ?? data.approvedApplications ?? 0,
            outstanding: data.outstandingAmount ?? data.totalLoanAmount ?? 0,
            repaymentRate: data.repaymentRate ?? 0,
          });
        }

        // Fetch pending payments count
        const paymentsResponse = await adminAPI.getPayments({ status: 'pending', limit: 100 });
        if (paymentsResponse.success) {
          setBadges(prev => ({
            ...prev,
            payments: paymentsResponse.data.payments.length || 0
          }));
        }
      } catch (error) {
        console.error('Error fetching badge counts:', error);
      }
    };

    fetchBadges();
    // Refresh badges every 2 minutes (was 30s — hammered rate limits when
    // navigating between admin sections that also call /admin/*).
    const interval = setInterval(fetchBadges, 120000);
    return () => clearInterval(interval);
  }, []);
  const renderIcon = (iconType: string) => {
    const iconProps = { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2" };
    
    switch (iconType) {
      case '📊':
      case 'dashboard':
        return (
          <svg {...iconProps}>
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
        );
      case 'applications':
        return (
          <svg {...iconProps}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
        );
      case '👥':
      case 'users':
        return (
          <svg {...iconProps}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        );
      case 'payments':
        return (
          <svg {...iconProps}>
            <path d="M12 2l10 5-10 5L2 7l10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        );
      case '📄':
      case 'documents':
        return (
          <svg {...iconProps}>
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
            <polyline points="13,2 13,9 20,9"/>
          </svg>
        );
      case 'mandates':
        return (
          <svg {...iconProps}>
            <path d="M20 7L9 18l-5-5"/>
            <path d="M4 4h16v4H4z"/>
          </svg>
        );
      case '📈':
      case 'reports':
        return (
          <svg {...iconProps}>
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
        );
      case '⚙️':
      case 'settings':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
          </svg>
        );
      default:
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="10"/>
          </svg>
        );
    }
  };

  // Use real badge counts from API
  const navItems: NavItem[] = [
    {
      id: 'overview',
      label: 'Dashboard',
      icon: '📊',
      description: 'Overview and key metrics'
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: 'applications',
      badge: badges.applications > 0 ? badges.applications : undefined,
      description: 'Pending loan applications'
    },
    {
      id: 'users',
      label: 'Users',
      icon: '👥',
      description: 'User management and profiles'
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: 'payments',
      badge: badges.payments > 0 ? badges.payments : undefined,
      description: 'Payment tracking and issues'
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: '📄',
      badge: badges.documents > 0 ? badges.documents : undefined,
      description: 'Document verification queue'
    },
    {
      id: 'mandates',
      label: 'DebiCheck',
      icon: 'mandates',
      description: 'Netcash DebiCheck mandates'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: '📈',
      description: 'Analytics and reporting'
    }
  ];

  return (
    <div className={`admin-sidebar ${collapsed && !isMobileDrawer ? 'collapsed' : ''} ${isMobileDrawer ? 'mobile-drawer' : ''} ${isMobileDrawer && !collapsed ? 'drawer-open' : ''}`}>
      {/* Logo Section */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <img src="/genlend-logo.png" alt="GenLend" className="logo-icon" style={{ width: '46px', height: '46px', borderRadius: '50%' }} />
          {showLabels && <span className="logo-text">GenLend</span>}
        </div>
        {isMobileDrawer && !collapsed && (
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onToggleCollapse}
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav" aria-label="Admin menu">
        <ul className="nav-list">
          {navItems.map((item) => (
            <li key={item.id} className="nav-item">
              <button
                className={`nav-link ${currentView === item.id ? 'active' : ''}`}
                onClick={() => onViewChange(item.id)}
                type="button"
                title={collapsed && !isMobileDrawer ? item.label : ''}
              >
                <span className="nav-icon">{renderIcon(item.icon)}</span>
                
                {showLabels && (
                  <>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                  </>
                )}
                
                {collapsed && !isMobileDrawer && item.badge && (
                  <span className="nav-badge-collapsed">{item.badge}</span>
                )}
              </button>
              
              {collapsed && !isMobileDrawer && (
                <div className="nav-tooltip">
                  <div className="tooltip-content">
                    <span className="tooltip-title">{item.label}</span>
                    <span className="tooltip-description">{item.description}</span>
                    {item.badge && (
                      <span className="tooltip-badge">{item.badge} pending</span>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Quick Stats (when expanded) */}
      {showLabels && (
        <div className="sidebar-stats">
          <h3 className="stats-title">Quick Stats</h3>
          <div className="sidebar-stats-grid">
            <div className="sidebar-stat-item">
              <span className="stat-label">Active Loans</span>
              <span className="stat-value">{quickStats.activeLoans}</span>
            </div>
            <div className="sidebar-stat-item">
              <span className="stat-label">Outstanding</span>
              <span className="stat-value">
                R{Math.round(quickStats.outstanding || 0).toLocaleString('en-ZA')}
              </span>
            </div>
            <div className="sidebar-stat-item">
              <span className="stat-label">Repayment Rate</span>
              <span className="stat-value">{Number(quickStats.repaymentRate || 0).toFixed(0)}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Support Section */}
      {showLabels && (
        <div className="sidebar-support">
          <div className="support-card">
            <button className="support-btn" type="button">
              💬 Need Help?
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSidebar;

